import { z } from 'zod';

/**
 * Limits a club may choose for its sign-in sessions. The specification's defaults (30 minutes for
 * staff, 12 hours for family accounts, seven days absolute) apply until a club changes them; the
 * ranges keep a club from configuring a session that never expires or one that cannot be used.
 */
export const sessionPolicyBounds = {
  staffIdleMinutes: { minimum: 5, maximum: 120, fallback: 30 },
  familyIdleMinutes: { minimum: 15, maximum: 24 * 60, fallback: 12 * 60 },
  absoluteDays: { minimum: 1, maximum: 7, fallback: 7 },
} as const;

export interface SessionPolicy {
  staffIdleMinutes: number;
  familyIdleMinutes: number;
  absoluteDays: number;
}

export const defaultSessionPolicy: SessionPolicy = {
  staffIdleMinutes: sessionPolicyBounds.staffIdleMinutes.fallback,
  familyIdleMinutes: sessionPolicyBounds.familyIdleMinutes.fallback,
  absoluteDays: sessionPolicyBounds.absoluteDays.fallback,
};

/** Stored columns are nullable ("use the default"); this fills the gaps. */
export function resolveSessionPolicy(stored: {
  staffIdleMinutes: number | null;
  familyIdleMinutes: number | null;
  absoluteDays: number | null;
}): SessionPolicy {
  return {
    staffIdleMinutes: stored.staffIdleMinutes ?? defaultSessionPolicy.staffIdleMinutes,
    familyIdleMinutes: stored.familyIdleMinutes ?? defaultSessionPolicy.familyIdleMinutes,
    absoluteDays: stored.absoluteDays ?? defaultSessionPolicy.absoluteDays,
  };
}

function boundedMinutes(bounds: { minimum: number; maximum: number }): z.ZodNumber {
  return z.number().int().min(bounds.minimum).max(bounds.maximum);
}

/** Replaces the club's session policy; every field is required so a save is always complete. */
export const updateSessionPolicyRequestSchema = z.strictObject({
  staffIdleMinutes: boundedMinutes(sessionPolicyBounds.staffIdleMinutes),
  familyIdleMinutes: boundedMinutes(sessionPolicyBounds.familyIdleMinutes),
  absoluteDays: boundedMinutes(sessionPolicyBounds.absoluteDays),
});
export type UpdateSessionPolicyRequest = z.infer<typeof updateSessionPolicyRequestSchema>;

export interface SessionPolicyView extends SessionPolicy {
  /** Organization row version; send it in `If-Match` when saving. */
  version: number;
}
